// API service for backend communication
// Use direct API Gateway URL in production to bypass CloudFront caching/routing issues
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 
  (process.env.NODE_ENV === 'production' 
    ? 'https://3amqzkaklb.execute-api.us-east-1.amazonaws.com/prod/api'
    : 'http://localhost:3001/api');

export class ApiService {
  private static getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  }

  private static async handleResponse(response: Response) {
    // Check if response is HTML (404 page) instead of JSON
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      throw new Error('Endpoint not implemented - falling back to demo mode');
    }

    if (!response.ok) {
      if (response.status === 401) {
        // Token expired or invalid
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
        window.location.href = '/admin/login';
        throw new Error('Authentication required');
      }
      
      const errorData = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(errorData.message || `HTTP ${response.status}`);
    }
    
    return response.json();
  }

  private static async safeApiCall(url: string, options: RequestInit = {}) {
    try {
      const response = await fetch(url, options);
      return await this.handleResponse(response);
    } catch (error) {
      console.warn(`API call failed for ${url}:`, error);
      // Return demo data structure
      return { success: false, error: 'API not available', demo: true };
    }
  }

  // Authentication
  static async loginWithPin(email: string, pin: string) {
    const result = await this.safeApiCall(`${API_BASE_URL}/pin-login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, pin }),
    });
    
    if (result.demo) {
      // Return demo authentication result
      return {
        success: true,
        data: {
          token: 'demo-jwt-token-' + Date.now(),
          user: {
            id: 'demo-user-1',
            email: email,
            full_name: 'Demo User',
            role: email.includes('admin') ? 'admin' : 'guest',
            groups: email.includes('admin') ? ['admins'] : ['guests']
          }
        }
      };
    }
    
    return result;
  }

  // Guest Management
  static async getGuests() {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/guests`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      // Return demo data in expected format
      return {
        success: true,
        data: {
          guests: [
            { id: '1', full_name: 'Sarah Johnson', email: 'sarah@example.com', phone_number: '+1-555-0001', oauth_provider: 'google', status: 'active', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
            { id: '2', full_name: 'Mike Chen', email: 'mike@example.com', phone_number: '+1-555-0002', oauth_provider: 'google', status: 'active', created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
          ]
        }
      };
    }
    
    return result;
  }

  static async createGuest(guestData: any) {
    const response = await fetch(`${API_BASE_URL}/guests`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(guestData),
    });
    return this.handleResponse(response);
  }

  static async updateGuest(guestId: string, guestData: any) {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/guests/${guestId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(guestData),
    });
    
    if (result.demo) {
      return {
        success: true,
        message: 'Guest updated successfully (demo mode)'
      };
    }
    
    return result;
  }

  static async deleteGuest(guestId: string) {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/guests/${guestId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      return {
        success: true,
        message: 'Guest deleted successfully (demo mode)'
      };
    }
    
    return result;
  }

  static async getGuestEvents(guestId: string) {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/guests/${guestId}/events`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      return {
        success: true,
        data: {
          events: [
            {
              id: 'event-001',
              name: 'Birthday Dinner',
              event_date: '2024-12-25',
              start_time: '18:00',
              is_mandatory: true,
              registration_status: 'registered',
              registered_at: new Date().toISOString()
            },
            {
              id: 'event-002',
              name: 'Pool Party',
              event_date: '2024-12-26',
              start_time: '14:00',
              is_mandatory: false,
              registration_status: null,
              registered_at: null
            }
          ]
        }
      };
    }
    
    return result;
  }

  static async updateGuestEventRegistration(guestId: string, eventId: string, action: 'register' | 'unregister') {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/guests/${guestId}/events/${eventId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ action }),
    });
    
    if (result.demo) {
      return {
        success: true,
        message: `Guest ${action === 'register' ? 'registered for' : 'unregistered from'} event successfully (demo mode)`
      };
    }
    
    return result;
  }



  static async approveAccessRequest(requestId: string) {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/access-requests/${requestId}/approve`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ status: 'approved' }),
    });
    
    if (result.demo) {
      // Update localStorage for demo mode
      const storedRequests = JSON.parse(localStorage.getItem('demo-access-requests') || '[]');
      const updatedRequests = storedRequests.map((req: any) => 
        req.id === requestId ? { ...req, status: 'approved' } : req
      );
      localStorage.setItem('demo-access-requests', JSON.stringify(updatedRequests));
      
      return {
        success: true,
        message: 'Access request approved successfully'
      };
    }
    
    return result;
  }

  static async rejectAccessRequest(requestId: string) {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/access-requests/${requestId}/reject`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ status: 'rejected' }),
    });
    
    if (result.demo) {
      // Update localStorage for demo mode
      const storedRequests = JSON.parse(localStorage.getItem('demo-access-requests') || '[]');
      const updatedRequests = storedRequests.map((req: any) => 
        req.id === requestId ? { ...req, status: 'rejected' } : req
      );
      localStorage.setItem('demo-access-requests', JSON.stringify(updatedRequests));
      
      return {
        success: true,
        message: 'Access request rejected successfully'
      };
    }
    
    return result;
  }

  static async clearAllRequests() {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/requests/clear-all`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      // Clear localStorage for demo mode
      localStorage.removeItem('demo-access-requests');
      
      return {
        success: true,
        message: 'All requests cleared successfully'
      };
    }
    
    return result;
  }

  // Media Management
  static async getMedia() {
    const response = await fetch(`${API_BASE_URL}/media`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  static async uploadMedia(formData: FormData) {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/media/upload`, {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        // Don't set Content-Type for FormData, let browser set it with boundary
      },
      body: formData,
    });
    return this.handleResponse(response);
  }

  static async deleteMedia(mediaId: string) {
    const response = await fetch(`${API_BASE_URL}/media/${mediaId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // Events Management
  static async getEvents() {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/events`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      return {
        success: true,
        data: {
          events: [
            {
              id: 'event-001',
              name: 'Birthday Dinner',
              eventDate: '2024-12-25',
              startTime: '18:00',
              endTime: '22:00',
              location: 'Main Hall',
              description: 'Main celebration dinner with family and friends',
              maxCapacity: 50,
              isMandatory: true
            },
            {
              id: 'event-002',
              name: 'Pool Party',
              eventDate: '2024-12-26',
              startTime: '14:00',
              endTime: '17:00',
              location: 'Pool Area',
              description: 'Afternoon pool activities and games',
              maxCapacity: 30,
              isMandatory: false
            }
          ]
        }
      };
    }
    
    return result;
  }

  static async createEvent(eventData: any) {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/events`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(eventData),
    });
    
    if (result.demo) {
      return {
        success: true,
        message: 'Event created successfully (demo mode)',
        data: { id: 'demo-event-' + Date.now(), ...eventData }
      };
    }
    
    return result;
  }

  static async updateEvent(eventId: string, eventData: any) {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/events/${eventId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(eventData),
    });
    
    if (result.demo) {
      return {
        success: true,
        message: 'Event updated successfully (demo mode)'
      };
    }
    
    return result;
  }

  static async deleteEvent(eventId: string) {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/events/${eventId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      return {
        success: true,
        message: 'Event deleted successfully (demo mode)'
      };
    }
    
    return result;
  }

  static async uploadEventImage(eventId: string, file: File, retryCount = 0): Promise<any> {
    const MAX_RETRIES = 2;
    const RETRY_DELAY = 1000; // 1 second

    try {
      const formData = new FormData();
      formData.append('image', file);

      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/admin/events/${eventId}/image`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          // Don't set Content-Type for FormData, let browser set it with boundary
        },
        body: formData,
      });

      // Handle specific error cases
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Network or server errors - retry
        if (response.status >= 500 || response.status === 503) {
          if (retryCount < MAX_RETRIES) {
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)));
            return this.uploadEventImage(eventId, file, retryCount + 1);
          }
          throw new Error(errorData.error || 'Image service temporarily unavailable. Please try again later.');
        }
        
        // Client errors - don't retry
        if (response.status === 400) {
          throw new Error(errorData.error || 'Invalid image file. Please check the file type and size.');
        }
        
        if (response.status === 401 || response.status === 403) {
          throw new Error('Authentication required. Please log in again.');
        }
        
        throw new Error(errorData.error || 'Failed to upload image. Please try again.');
      }

      return this.handleResponse(response);
    } catch (error) {
      // Network errors (no response from server)
      if (error instanceof TypeError && error.message.includes('fetch')) {
        if (retryCount < MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)));
          return this.uploadEventImage(eventId, file, retryCount + 1);
        }
        throw new Error('Network error. Please check your connection and try again.');
      }
      
      // Re-throw other errors
      throw error;
    }
  }

  static async deleteEventImage(eventId: string, retryCount = 0): Promise<any> {
    const MAX_RETRIES = 2;
    const RETRY_DELAY = 1000; // 1 second

    try {
      const response = await fetch(`${API_BASE_URL}/admin/events/${eventId}/image`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });

      // Handle specific error cases
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Network or server errors - retry
        if (response.status >= 500 || response.status === 503) {
          if (retryCount < MAX_RETRIES) {
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)));
            return this.deleteEventImage(eventId, retryCount + 1);
          }
          throw new Error(errorData.error || 'Image service temporarily unavailable. Please try again later.');
        }
        
        // Client errors - don't retry
        if (response.status === 404) {
          throw new Error('Image not found or already deleted.');
        }
        
        if (response.status === 401 || response.status === 403) {
          throw new Error('Authentication required. Please log in again.');
        }
        
        throw new Error(errorData.error || 'Failed to remove image. Please try again.');
      }

      return this.handleResponse(response);
    } catch (error) {
      // Network errors (no response from server)
      if (error instanceof TypeError && error.message.includes('fetch')) {
        if (retryCount < MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)));
          return this.deleteEventImage(eventId, retryCount + 1);
        }
        throw new Error('Network error. Please check your connection and try again.');
      }
      
      // Re-throw other errors
      throw error;
    }
  }

  // Analytics
  static async getAnalytics() {
    const response = await fetch(`${API_BASE_URL}/analytics`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  static async getUsageAnalytics() {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/analytics/usage`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      return {
        success: true,
        data: {
          guests: { total_guests: 2, active_guests: 2, new_guests_week: 1 },
          events: { total_events: 4, guests_with_registrations: 20, avg_registrations_per_event: 5.0 },
          media: { total_media: 42, approved_media: 35, featured_media: 8, new_media_week: 6 },
          requests: { event_change_requests: 2, size_change_requests: 1, pending_requests: 3, approved_requests: 18 },
          generatedAt: new Date().toISOString()
        }
      };
    }
    
    return result;
  }

  static async getEngagementAnalytics() {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/analytics/engagement`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      return {
        success: true,
        data: {
          eventEngagement: [],
          mediaEngagement: [],
          requestActivity: [],
          generatedAt: new Date().toISOString()
        }
      };
    }
    
    return result;
  }

  // Request Management
  static async getAccessRequests() {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/access-requests`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      // Get stored requests from localStorage for demo mode
      const storedRequests = JSON.parse(localStorage.getItem('demo-access-requests') || '[]');
      return {
        success: true,
        data: {
          requests: storedRequests.length > 0 ? storedRequests : [
            {
              id: 'req-001',
              email: 'alice@example.com',
              full_name: 'Alice Johnson',
              phone_number: '+1-555-0123',
              oauth_provider: 'google',
              status: 'pending',
              created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
            }
          ]
        }
      };
    }
    
    return result;
  }

  static async getEventChangeRequests() {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/event-changes`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      return {
        success: true,
        data: {
          requests: [
            {
              id: 'evt-001',
              guest_id: 'guest-001',
              guest_name: 'Sarah Wilson',
              request_type: 'event_registration',
              current_value: 'registered',
              requested_value: 'unregistered',
              reason: 'Schedule conflict',
              status: 'pending',
              created_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()
            }
          ]
        }
      };
    }
    
    // Normalize the response format - backend returns { success, requests } but we need { success, data: { requests } }
    if (result.success && result.requests) {
      return {
        success: true,
        data: {
          requests: result.requests
        }
      };
    }
    
    return result;
  }

  static async getSizeChangeRequests() {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/size-change-requests`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      return {
        success: true,
        data: {
          requests: [
            {
              id: 'size-001',
              guest_id: 'guest-003',
              guest_name: 'Emma Thompson',
              request_type: 'clothing_size',
              current_value: 'M',
              requested_value: 'L',
              reason: 'Size runs small',
              status: 'pending',
              created_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString()
            }
          ]
        }
      };
    }
    
    // Normalize the response format - backend returns { success, requests } but we need { success, data: { requests } }
    if (result.success && result.requests) {
      return {
        success: true,
        data: {
          requests: result.requests
        }
      };
    }
    
    return result;
  }

  // Get all change requests (unified endpoint including transportation)
  static async getChangeRequests() {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/change-requests`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      return {
        success: true,
        data: []
      };
    }
    
    // Backend returns { success, data: [...] }
    return result;
  }

  // Media Management
  static async getPendingMedia() {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/media/pending`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      return {
        success: true,
        data: {
          media: [
            {
              id: 'media-001',
              guest_id: 'guest-001',
              guest_name: 'Sarah Wilson',
              filename: 'birthday-photo-1.jpg',
              original_name: 'IMG_2024_party.jpg',
              file_type: 'image/jpeg',
              file_size: 2048000,
              storage_url: 'https://example.com/media/birthday-photo-1.jpg',
              is_approved: false,
              uploaded_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
            }
          ]
        }
      };
    }
    
    return result;
  }

  static async getMediaStats() {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/media/stats`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      return {
        success: true,
        data: {
          total_media: 42,
          pending_approval: 2,
          approved_media: 35,
          featured_media: 8,
          total_size_mb: 156.7
        }
      };
    }
    
    return result;
  }

  // PIN Management
  static async changePin(currentPin: string, newPin: string) {
    const result = await this.safeApiCall(`${API_BASE_URL}/change-pin`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ currentPin, newPin }),
    });
    
    if (result.demo) {
      return {
        success: true,
        message: 'PIN changed successfully (demo mode)'
      };
    }
    
    return result;
  }

  // Unified Guest Management (Optimized for 1000+ guests)
  static async getGuestsWithPinStatus(options: {
    page?: number;
    limit?: number;
    search?: string;
    pinStatus?: string;
    authMethod?: string;
    migrationStatus?: string;
    includeStats?: boolean;
    includeEventRegistrations?: boolean;
  } = {}) {
    const {
      page = 1,
      limit = 50,
      search,
      pinStatus,
      authMethod,
      migrationStatus,
      includeStats = true,
      includeEventRegistrations = false
    } = options;

    // Build query parameters
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      includeStats: includeStats.toString(),
      includeEventRegistrations: includeEventRegistrations.toString()
    });

    if (search) params.append('search', search);
    if (pinStatus) params.append('pinStatus', pinStatus);
    if (authMethod) params.append('authMethod', authMethod);
    if (migrationStatus) params.append('migrationStatus', migrationStatus);

    const result = await this.safeApiCall(`${API_BASE_URL}/unified-guest-management/guests?${params}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      // Return demo data for unified guest management with pagination
      return {
        success: true,
        data: {
          guests: [], // Will be populated by component demo data
          stats: {
            totalGuests: 0,
            withPins: 0,
            withPasswords: 0,
            withBoth: 0,
            needingMigration: 0,
            lockedAccounts: 0
          },
          total: 0,
          pagination: {
            page: 1,
            limit: 50,
            totalPages: 0,
            hasNextPage: false,
            hasPrevPage: false,
            totalItems: 0
          }
        }
      };
    }
    
    return result;
  }

  // Admin PIN Management
  static async getGuestsWithPins() {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/guests/pins`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      return {
        success: true,
        data: {
          guests: [
            {
              id: '1',
              email: 'sarah@example.com',
              full_name: 'Sarah Johnson',
              phone_number: '+1-555-0001',
              pin: '1234',
              pin_set_date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
              pin_changed_by: 'admin',
              failed_pin_attempts: 0,
              status: 'active',
              created_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString()
            },
            {
              id: '2',
              email: 'mike@example.com',
              full_name: 'Mike Chen',
              phone_number: '+1-555-0002',
              pin: '5678',
              pin_set_date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
              pin_changed_by: 'self',
              failed_pin_attempts: 1,
              status: 'active',
              created_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString()
            }
          ]
        }
      };
    }
    
    return result;
  }

  static async generatePin(guestId: string) {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/generate-pin`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ guestId }),
    });
    
    if (result.demo) {
      return {
        success: true,
        data: {
          pin: Math.floor(1000 + Math.random() * 9000).toString()
        }
      };
    }
    
    return result;
  }

  static async setPin(guestId: string, pin: string) {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/set-pin`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ guestId, pin }),
    });
    
    if (result.demo) {
      return {
        success: true,
        message: 'PIN set successfully (demo mode)'
      };
    }
    
    return result;
  }

  static async resetPin(guestId: string) {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/reset-pin`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ guestId }),
    });
    
    if (result.demo) {
      return {
        success: true,
        data: {
          pin: Math.floor(1000 + Math.random() * 9000).toString()
        }
      };
    }
    
    return result;
  }

  static async bulkEmailPins(guestIds: string[]) {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/bulk-email-pins`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ guestIds }),
    });
    
    if (result.demo) {
      return {
        success: true,
        data: {
          results: {
            success: guestIds.length,
            failed: 0,
            errors: []
          }
        }
      };
    }
    
    return result;
  }

  static async unlockAccount(guestId: string) {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/unlock-account`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ guestId }),
    });
    
    if (result.demo) {
      return {
        success: true,
        message: 'Account unlocked successfully (demo mode)'
      };
    }
    
    return result;
  }

  // PIN Analytics
  static async getPinAnalyticsDashboard(days: number = 7) {
    const result = await this.safeApiCall(`${API_BASE_URL}/pin-analytics/dashboard?days=${days}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      return {
        success: true,
        data: {
          overview: {
            timeframe: `${days} days`,
            generatedAt: new Date().toISOString()
          },
          metrics: {
            totalPinLogins: 156,
            successfulLogins: 142,
            failedLogins: 14,
            successRate: 91.03,
            uniqueUsers: 23,
            averageAttemptsPerUser: 6.78
          },
          securityMetrics: {
            accountsLocked: 2,
            accountsUnlocked: 1,
            activelyLockedAccounts: 1,
            multipleFailureGuests: 3,
            suspiciousActivityCount: 1
          },
          usagePatterns: {
            daily: Array.from({ length: days }, (_, i) => ({
              date: new Date(Date.now() - (days - 1 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              successful: Math.floor(Math.random() * 20) + 5,
              failed: Math.floor(Math.random() * 5),
              total: 0,
              successRate: 0
            })).map(day => ({
              ...day,
              total: day.successful + day.failed,
              successRate: Math.round((day.successful / (day.successful + day.failed)) * 100)
            })),
            hourly: Array.from({ length: 24 }, (_, hour) => ({
              hour,
              loginCount: Math.floor(Math.random() * 10) + 1,
              successRate: Math.floor(Math.random() * 20) + 80
            }))
          },
          securityAlerts: {
            alerts: [
              {
                id: 'alert-001',
                guestId: 'guest-001',
                guestEmail: 'sarah@example.com',
                alertType: 'MULTIPLE_FAILURES',
                severity: 'MEDIUM',
                description: '3 failed PIN attempts in recent period',
                timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
                resolved: false
              },
              {
                id: 'alert-002',
                guestId: 'guest-002',
                guestEmail: 'mike@example.com',
                alertType: 'ACCOUNT_LOCKED',
                severity: 'HIGH',
                description: 'Account locked due to multiple failed PIN attempts',
                timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
                resolved: false
              }
            ],
            summary: {
              total: 2,
              critical: 0,
              high: 1,
              medium: 1
            }
          },
          adminActivity: [
            {
              adminId: 'admin-001',
              pinsGenerated: 5,
              pinsChanged: 2,
              accountsUnlocked: 1,
              totalActions: 8,
              lastActivity: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString()
            }
          ]
        }
      };
    }
    
    return result;
  }

  static async getPinSecurityAlerts(hours: number = 24) {
    const result = await this.safeApiCall(`${API_BASE_URL}/pin-analytics/security-alerts?hours=${hours}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      return {
        success: true,
        data: {
          alerts: [
            {
              id: 'alert-001',
              guestId: 'guest-001',
              guestEmail: 'sarah@example.com',
              alertType: 'MULTIPLE_FAILURES',
              severity: 'MEDIUM',
              description: '3 failed PIN attempts in 24 hours',
              timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
              resolved: false
            }
          ],
          alertCount: 1,
          criticalCount: 0,
          highCount: 0
        }
      };
    }
    
    return result;
  }

  static async getGuestPinAnalytics(guestId: string, days: number = 30) {
    const result = await this.safeApiCall(`${API_BASE_URL}/pin-analytics/guest/${guestId}?days=${days}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      return {
        success: true,
        data: {
          metrics: {
            totalAttempts: 15,
            successfulLogins: 12,
            failedAttempts: 3,
            successRate: 80.0,
            lastSuccessfulLogin: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
            accountCurrentlyLocked: false
          },
          recentActivity: [
            {
              action: 'PIN_LOGIN_SUCCESS',
              timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
              success: true,
              ipAddress: '192.168.1.100'
            },
            {
              action: 'PIN_LOGIN_FAILED',
              timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
              success: false,
              ipAddress: '192.168.1.100'
            }
          ],
          securityEvents: [
            {
              action: 'PIN_CHANGED',
              timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
              adminId: 'admin-001'
            }
          ]
        }
      };
    }
    
    return result;
  }

  static async resolvePinAlert(alertId: string, resolution?: string, notes?: string) {
    const result = await this.safeApiCall(`${API_BASE_URL}/pin-analytics/alerts/${alertId}/resolve`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ resolution, notes }),
    });
    
    if (result.demo) {
      return {
        success: true,
        message: 'Alert resolved successfully (demo mode)'
      };
    }
    
    return result;
  }

  // Health check
  static async healthCheck() {
    const response = await fetch(`${API_BASE_URL}/health`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // Guest Access Request
  static async submitGuestAccessRequest(requestData: {
    fullName: string;
    email: string;
    phone?: string;
    relationshipToHost: string;
    message?: string;
  }) {
    const result = await this.safeApiCall(`${API_BASE_URL}/guest-access/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(requestData),
    });
    
    if (result.demo) {
      // Store in localStorage for demo mode so it appears in admin dashboard
      const requestId = 'demo-request-' + Date.now();
      const newRequest = {
        id: requestId,
        email: requestData.email,
        full_name: requestData.fullName,
        phone_number: requestData.phone || '',
        oauth_provider: 'email',
        status: 'pending',
        created_at: new Date().toISOString(),
        relationshipToHost: requestData.relationshipToHost,
        message: requestData.message || ''
      };
      
      // Get existing requests and add the new one
      const existingRequests = JSON.parse(localStorage.getItem('demo-access-requests') || '[]');
      existingRequests.push(newRequest);
      localStorage.setItem('demo-access-requests', JSON.stringify(existingRequests));
      
      return {
        success: true,
        message: 'Access request submitted successfully',
        data: {
          id: requestId,
          status: 'pending',
          requestedAt: new Date().toISOString()
        }
      };
    }
    
    return result;
  }

  // Bulk PIN Operations (Optimized for 100+ guests)
  static async bulkPinOperations(
    guestIds: string[], 
    operation: 'generate' | 'reset' | 'email',
    options: {
      enableProgressTracking?: boolean;
      maxRetries?: number;
      enableEmailNotification?: boolean;
    } = {}
  ) {
    return this.safeApiCall(`${API_BASE_URL}/enhanced-pin-operations/bulk`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        guestIds,
        operation,
        enableProgressTracking: options.enableProgressTracking || false,
        maxRetries: options.maxRetries || 2,
        enableEmailNotification: options.enableEmailNotification !== false
      }),
    });
  }

  // Enhanced Migration Operations with >95% success rate
  static async bulkMigrateToPin(
    guestIds: string[],
    options: {
      migrationMode?: 'gradual' | 'immediate';
      emailNotification?: boolean;
      preservePasswords?: boolean;
      requireHighSuccessRate?: boolean;
    } = {}
  ) {
    const result = await this.safeApiCall(`${API_BASE_URL}/pin-migration/migrate`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        batchSize: Math.min(25, Math.ceil(guestIds.length / 4)), // Optimal batch size
        sendNotifications: options.emailNotification !== false,
        dryRun: false,
        includeExistingPinUsers: false,
        requireHighSuccessRate: options.requireHighSuccessRate !== false
      }),
    });
    
    if (result.demo) {
      return {
        success: true,
        data: {
          migrationId: 'demo-migration-' + Date.now(),
          totalGuests: guestIds.length,
          migratedGuests: guestIds.length,
          skippedGuests: 0,
          failedGuests: 0,
          successRate: 100,
          meetsTarget: true,
          errors: []
        }
      };
    }
    
    return result;
  }

  // Migration Readiness Check
  static async checkMigrationReadiness() {
    const result = await this.safeApiCall(`${API_BASE_URL}/pin-migration/readiness`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      return {
        success: true,
        data: {
          ready: true,
          preconditions: {
            valid: true,
            errors: []
          },
          statistics: {
            totalGuests: 10,
            guestsWithPins: 3,
            guestsWithoutPins: 7,
            migrationCoverage: 30
          },
          recommendations: {
            optimalBatchSize: 10,
            estimatedDuration: 2,
            riskLevel: 'low'
          }
        }
      };
    }
    
    return result;
  }

  // Migration Preview
  static async previewMigration(includeExistingPinUsers: boolean = false) {
    const result = await this.safeApiCall(`${API_BASE_URL}/pin-migration/preview?includeExistingPinUsers=${includeExistingPinUsers}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      return {
        success: true,
        data: {
          migrationId: 'demo-preview-' + Date.now(),
          totalGuests: 7,
          migratedGuests: 7,
          skippedGuests: 0,
          failedGuests: 0,
          projectedSuccessRate: 100,
          meetsTarget: true,
          preview: true,
          errors: []
        }
      };
    }
    
    return result;
  }

  // Migration Status Tracking
  static async getMigrationStatus(migrationId: string) {
    const result = await this.safeApiCall(`${API_BASE_URL}/pin-migration/status/${migrationId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      return {
        success: true,
        data: {
          migrationId,
          status: 'completed',
          startedAt: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
          completedAt: new Date().toISOString(),
          totalGuests: 10,
          processedGuests: 10,
          migratedGuests: 10,
          skippedGuests: 0,
          failedGuests: 0,
          errors: []
        }
      };
    }
    
    return result;
  }

  // Migration Statistics
  static async getMigrationStatistics() {
    const result = await this.safeApiCall(`${API_BASE_URL}/pin-migration/statistics`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      return {
        success: true,
        data: {
          totalGuests: 15,
          guestsWithPins: 8,
          guestsWithoutPins: 7,
          migrationCoverage: 53.33
        }
      };
    }
    
    return result;
  }

  // Performance Monitoring
  static async getBulkOperationPerformance(hours: number = 24) {
    return this.safeApiCall(`${API_BASE_URL}/enhanced-pin-operations/performance?hours=${hours}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
  }

  // Health Check for Bulk Operations
  static async getBulkOperationHealth() {
    return this.safeApiCall(`${API_BASE_URL}/enhanced-pin-operations/health`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
  }

  // Generic POST method for compatibility layer
  static async post(endpoint: string, data: any) {
    // Prepend API_BASE_URL if endpoint doesn't start with http
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
    return this.safeApiCall(url, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
  }

  // Generic GET method for compatibility layer
  static async get(endpoint: string, params?: any) {
    // Prepend API_BASE_URL if endpoint doesn't start with http
    let url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          searchParams.append(key, params[key].toString());
        }
      });
      url += `?${searchParams.toString()}`;
    }
    
    return this.safeApiCall(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
  }

  // Generic PUT method for compatibility layer
  static async put(endpoint: string, data: any) {
    // Prepend API_BASE_URL if endpoint doesn't start with http
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
    return this.safeApiCall(url, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
  }

  // Generic DELETE method for compatibility layer
  static async delete(endpoint: string) {
    // Prepend API_BASE_URL if endpoint doesn't start with http
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
    return this.safeApiCall(url, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
  }

  // Clothing Assignment Management (Admin)
  static async getGuestClothingAssignments(guestId: string) {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/guests/${guestId}/clothing`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      // Get from localStorage for demo mode
      const storageKey = `demo-clothing-${guestId}`;
      const storedAssignments = JSON.parse(localStorage.getItem(storageKey) || '[]');
      
      // If no stored assignments, return default demo data
      const assignments = storedAssignments.length > 0 ? storedAssignments : [
        {
          id: 'clothing-001',
          guestId: guestId,
          eventId: 'event-001',
          eventName: 'Birthday Dinner',
          itemType: 'T-Shirt',
          size: 'L',
          color: 'Navy Blue',
          status: 'assigned',
          assignedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
          pickupInstructions: 'Available at registration desk'
        },
        {
          id: 'clothing-002',
          guestId: guestId,
          eventId: 'event-001',
          eventName: 'Birthday Dinner',
          itemType: 'Golf Polo Shirt',
          size: 'M',
          color: 'White',
          status: 'assigned',
          assignedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
          pickupInstructions: 'Available at registration desk'
        },
        {
          id: 'clothing-003',
          guestId: guestId,
          eventId: 'event-002',
          eventName: 'Pool Party',
          itemType: 'African Style Cargo Pants',
          size: 'Regular Length & Extra Wide Waist',
          color: 'Khaki',
          status: 'assigned',
          assignedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          pickupInstructions: 'Available at front desk'
        }
      ];
      
      return {
        success: true,
        data: {
          assignments
        }
      };
    }
    
    return result;
  }

  static async addClothingAssignment(guestId: string, assignmentData: {
    eventId: string;
    itemType: string;
    size: string;
    color?: string;
    pickupInstructions?: string;
  }) {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/guests/${guestId}/clothing`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(assignmentData),
    });
    
    if (result.demo) {
      // Store in localStorage for demo mode persistence
      const storageKey = `demo-clothing-${guestId}`;
      const existingAssignments = JSON.parse(localStorage.getItem(storageKey) || '[]');
      
      const newAssignment = {
        id: 'clothing-' + Date.now(),
        guestId: guestId,
        ...assignmentData,
        status: 'assigned',
        assignedAt: new Date().toISOString()
      };
      
      existingAssignments.push(newAssignment);
      localStorage.setItem(storageKey, JSON.stringify(existingAssignments));
      
      return {
        success: true,
        data: {
          assignment: newAssignment
        },
        message: 'Clothing assignment created successfully (demo mode)'
      };
    }
    
    return result;
  }

  static async updateClothingAssignment(assignmentId: string, updateData: {
    size?: string;
    itemType?: string;
    color?: string;
    pickupInstructions?: string;
    status?: string;
  }) {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/clothing-assignments/${assignmentId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(updateData),
    });
    
    if (result.demo) {
      // Update in localStorage for demo mode
      // Find the assignment across all guests
      const allKeys = Object.keys(localStorage).filter(key => key.startsWith('demo-clothing-'));
      
      for (const key of allKeys) {
        const assignments = JSON.parse(localStorage.getItem(key) || '[]');
        const index = assignments.findIndex((a: any) => a.id === assignmentId);
        
        if (index !== -1) {
          assignments[index] = { ...assignments[index], ...updateData };
          localStorage.setItem(key, JSON.stringify(assignments));
          break;
        }
      }
      
      return {
        success: true,
        message: 'Clothing assignment updated successfully (demo mode)'
      };
    }
    
    return result;
  }

  static async removeClothingAssignment(assignmentId: string) {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/clothing-assignments/${assignmentId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      // Remove from localStorage for demo mode
      const allKeys = Object.keys(localStorage).filter(key => key.startsWith('demo-clothing-'));
      
      for (const key of allKeys) {
        const assignments = JSON.parse(localStorage.getItem(key) || '[]');
        const filtered = assignments.filter((a: any) => a.id !== assignmentId);
        
        if (filtered.length !== assignments.length) {
          localStorage.setItem(key, JSON.stringify(filtered));
          break;
        }
      }
      
      return {
        success: true,
        message: 'Clothing assignment removed successfully (demo mode)'
      };
    }
    
    return result;
  }

  // Site Customization Settings
  static async getSiteSettings() {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/site-settings`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      // Return demo settings
      const defaultSettings = {
        id: 'draft',
        version: '1.0.0',
        theme: {
          colors: {
            primary: '#1976d2',
            secondary: '#dc004e',
            accent: '#f50057',
            background: '#ffffff',
            text: '#000000'
          },
          fonts: {
            heading: 'Roboto',
            body: 'Roboto',
            headingSize: 24,
            bodySize: 16
          }
        },
        content: {
          welcome: {
            title: 'Welcome to Our Event',
            message: 'We\'re excited to have you join us!',
            tagline: 'Making Memories Together'
          },
          footer: {
            contactEmail: 'info@example.com',
            socialLinks: {}
          }
        },
        images: {},
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      return {
        success: true,
        data: {
          draft: defaultSettings,
          published: defaultSettings,
          lastPublished: new Date().toISOString(),
          lastModified: new Date().toISOString()
        }
      };
    }
    
    return result;
  }

  static async saveSiteSettings(settings: any, saveAs: 'draft' | 'published') {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/site-settings`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ settings, saveAs }),
    });
    
    if (result.demo) {
      return {
        success: true,
        data: {
          settings,
          version: Date.now().toString()
        },
        message: 'Settings saved successfully (demo mode)'
      };
    }
    
    return result;
  }

  static async publishSiteSettings(version?: string) {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/site-settings/publish`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ version }),
    });
    
    if (result.demo) {
      return {
        success: true,
        data: {
          version: Date.now().toString(),
          publishedAt: new Date().toISOString()
        },
        message: 'Settings published successfully (demo mode)'
      };
    }
    
    return result;
  }

  static async getSiteSettingsHistory() {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/site-settings/history`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      return {
        success: true,
        data: {
          versions: [
            {
              version: '1.0.0',
              publishedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
              publishedBy: 'admin-001',
              publishedByEmail: 'admin@example.com',
              settings: {
                id: 'version-1',
                version: '1.0.0',
                theme: {
                  colors: {
                    primary: '#1976d2',
                    secondary: '#dc004e',
                    accent: '#f50057',
                    background: '#ffffff',
                    text: '#000000'
                  },
                  fonts: {
                    heading: 'Roboto',
                    body: 'Roboto',
                    headingSize: 24,
                    bodySize: 16
                  }
                },
                content: {
                  welcome: {
                    title: 'Welcome to Our Event',
                    message: 'We\'re excited to have you join us!',
                    tagline: 'Making Memories Together'
                  },
                  footer: {
                    contactEmail: 'info@example.com',
                    socialLinks: {}
                  }
                },
                images: {},
                createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
                updatedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
              },
              changesSummary: 'Initial version'
            }
          ]
        }
      };
    }
    
    return result;
  }

  static async uploadSiteImage(file: File, imageType: 'logo' | 'hero' | 'background') {
    const formData = new FormData();
    formData.append('image', file);

    const token = localStorage.getItem('authToken');
    
    try {
      const response = await fetch(`${API_BASE_URL}/site-customization/images/${imageType}`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          // Don't set Content-Type for FormData, let browser set it with boundary
        },
        body: formData,
      });

      return this.handleResponse(response);
    } catch (error) {
      // Demo mode fallback
      return {
        success: true,
        data: {
          imageUrl: URL.createObjectURL(file),
          imageType
        },
        message: 'Image uploaded successfully (demo mode)'
      };
    }
  }

  static async getPublicSiteSettings() {
    // Use direct API Gateway URL for site settings to bypass CloudFront routing issues
    const apiUrl = process.env.NODE_ENV === 'production' 
      ? 'https://3amqzkaklb.execute-api.us-east-1.amazonaws.com/prod/api/site-settings/public'
      : `${API_BASE_URL}/site-settings/public`;
    
    const result = await this.safeApiCall(apiUrl, {
      method: 'GET',
    });
    
    if (result.demo) {
      return {
        success: true,
        data: {
          theme: {
            colors: {
              primary: '#1976d2',
              secondary: '#dc004e',
              accent: '#f50057',
              background: '#ffffff',
              text: '#000000'
            },
            fonts: {
              heading: 'Roboto',
              body: 'Roboto',
              headingSize: 24,
              bodySize: 16
            }
          },
          content: {
            welcome: {
              title: 'Welcome to Our Event',
              message: 'We\'re excited to have you join us!',
              tagline: 'Making Memories Together'
            },
            footer: {
              contactEmail: 'info@example.com',
              socialLinks: {}
            }
          },
          images: {},
          customCSS: ''
        }
      };
    }
    
    return result;
  }

  static async resetSiteSettings() {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/site-settings/reset`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    
    if (result.demo) {
      return {
        success: true,
        data: {
          draft: {
            id: 'draft',
            version: '1.0.0',
            theme: {
              colors: {
                primary: '#1976d2',
                secondary: '#dc004e',
                accent: '#f50057',
                background: '#ffffff',
                text: '#000000'
              },
              fonts: {
                heading: 'Roboto',
                body: 'Roboto',
                headingSize: 24,
                bodySize: 16
              }
            },
            content: {
              welcome: {
                title: 'Welcome to Our Event',
                message: 'We\'re excited to have you join us!',
                tagline: 'Making Memories Together'
              },
              footer: {
                contactEmail: 'info@example.com',
                socialLinks: {}
              }
            },
            images: {},
            customCSS: '',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          },
          published: {
            id: 'published',
            version: '1.0.0',
            theme: {
              colors: {
                primary: '#1976d2',
                secondary: '#dc004e',
                accent: '#f50057',
                background: '#ffffff',
                text: '#000000'
              },
              fonts: {
                heading: 'Roboto',
                body: 'Roboto',
                headingSize: 24,
                bodySize: 16
              }
            },
            content: {
              welcome: {
                title: 'Welcome to Our Event',
                message: 'We\'re excited to have you join us!',
                tagline: 'Making Memories Together'
              },
              footer: {
                contactEmail: 'info@example.com',
                socialLinks: {}
              }
            },
            images: {},
            customCSS: '',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            publishedAt: new Date().toISOString()
          },
          imagesDeleted: 0
        },
        message: 'Settings reset to default successfully (demo mode)'
      };
    }
    
    return result;
  }

  static async restoreSiteSettingsVersion(versionId: string) {
    const result = await this.safeApiCall(`${API_BASE_URL}/admin/site-settings/restore`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ versionId }),
    });
    
    if (result.demo) {
      return {
        success: true,
        data: {
          version: `v${Date.now()}`,
          publishedAt: new Date().toISOString(),
          restoredFrom: versionId
        },
        message: 'Version restored successfully (demo mode)'
      };
    }
    
    return result;
  }
}

export default ApiService;